'use client';

import React from 'react';

import { useLocale } from 'next-intl';

import {
  CommentBox,
  CommentInfo,
  CommentDate,
  Separator,
  CommentContent as CommentWrapper,
  ReplyLink,
  ReplyFormWrapper,
  Action,
} from './Comment.styled';
import CommentAuthor from './CommentAuthor';
import CommentContent from './CommentContent';
import DeleteCommentButton from './DeleteCommentButton';
import { useAuthContext } from '../../AuthProvider/context';
import { Format } from '@/lib/dateTime/formatDate';
import Anchor from '@/ui/components/Anchor';
// import Avatar from '@/ui/components/Avatar';
import AddCommentForm from '@/ui/containers/CommentsSection/AddCommentForm';
import { getCurrentHash } from '@/utils/hashChange';

/**
 * @todo translate
 */
export default function Comment({ content, post_id, date_gmt_iso, role, user: userName, user_id, id }) {
  const { can, actions, isUserLoggedIn, user } = useAuthContext();

  const isTheSameUser = user?.id == user_id;

  const locale = useLocale();
  const [isReplying, setIsReplying] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [localTime, setLocalTime] = React.useState<string>('');
  const [relativeTime, setRelativeTime] = React.useState<string>('');
  const [isHighlighted, setIsHighlighted] = React.useState(false);

  const localisedDate = React.useMemo(() => {
    return new Format(date_gmt_iso).in(locale);
  }, [date_gmt_iso, locale]);

  React.useEffect(() => {
    setLocalTime(localisedDate.toString());
    setRelativeTime(localisedDate.toRelativeTime());
  }, [localisedDate]);

  React.useEffect(() => {
    const hash = `#${getCurrentHash()}`;
    if (hash.startsWith('#comment-')) {
      const hashId = hash.split('-').pop();
      setIsHighlighted(hashId === String(id));
    }
  }, [id]);

  const onReplySent = React.useCallback(() => {
    setIsReplying(false);
  }, []);

  const openReply = React.useCallback(() => {
    if (!isUserLoggedIn) {
      alert('Please log in to reply');
      return;
    }

    setIsReplying(true);
  }, [isUserLoggedIn]);

  const isYoungerThanOneDay = React.useMemo(() => {
    const oneDay = 24 * 60 * 60 * 1000;
    const now = new Date().getTime();
    const commentDate = new Date(date_gmt_iso).getTime();

    return now - commentDate < oneDay;
  }, [date_gmt_iso]);

  return (
    <>
      <Anchor id={`comment-${id}`}>
        <Anchor id={`comment-${post_id}-${id}`}>
          <CommentBox>
            {/* <Avatar src="https://via.placeholder.com/150" /> */}
            <CommentWrapper $highlighted={isHighlighted}>
              <CommentInfo>
                <CommentAuthor user={userName} role={role} />
                <Separator>•</Separator>
                <CommentDate title={localTime}>{relativeTime}</CommentDate>
              </CommentInfo>
              <CommentContent isEditing={isEditing} content={content} id={id} setIsEditing={setIsEditing} />
              <Action>
                <ReplyLink onClick={openReply}>Reply</ReplyLink>
                {isUserLoggedIn && isTheSameUser && isYoungerThanOneDay && (
                  <ReplyLink onClick={() => setIsEditing(true)}>Edit</ReplyLink>
                )}
                {isUserLoggedIn && (can(actions.delete_comment) || isTheSameUser) && (
                  <DeleteCommentButton commentId={id} />
                )}
              </Action>
            </CommentWrapper>
          </CommentBox>
        </Anchor>
      </Anchor>
      {isReplying && (
        <ReplyFormWrapper>
          <AddCommentForm parentCommentId={id} onSuccessCallback={onReplySent} />
        </ReplyFormWrapper>
      )}
    </>
  );
}
